/**
 *
 * LoginForm
 *
 */

import React from 'react'
import PropTypes from 'prop-types'
import { connect } from 'react-redux'
import { compose } from 'redux'
import { createStructuredSelector } from 'reselect'
import { Navigate } from 'react-router-dom'
import { SelectorUtils } from '@nv/react-commons/src/Utils'

import { DialogBox } from 'components/DialogBox'
import { getUrlParameter } from 'utils/url'
import { ROUTES } from '../Base/constants'
import { selectIsAuthenticated } from '../Base/selectors'
import injectSaga from 'utils/injectSaga'

import { userCreators } from '../Base/redux'
import { authMachine } from './machine'
import saga from './saga'
import ForgotEmailForm from '../ForgotEmailForm'
import ForgotPasswordForm from '../ForgotPasswordFormV1'
import { withLocation } from '../RouterHOCs'

const { selector } = SelectorUtils

const messages = {
  title: 'login',
  email: 'email',
  password: 'password',
  submit: 'login',
  errorLink: 'recover_email'
}

const defaultValues = {
  email: '',
  password: '',
  percent: 0
}

export class LoginForm extends React.Component {
  state = {
    authState: authMachine.initialState.value,
    ...defaultValues
  }

  static getDerivedStateFromProps (nextProps, prevState) {
    const { addShipper, isAuthenticated } = nextProps
    if (!addShipper && isAuthenticated) {
      const authState = authMachine.transition(prevState.authState, 'IS_AUTHENTICATED').value
      return {
        ...prevState,
        authState
      }
    }
    return null
  }

  componentDidMount () {
    const {
      location: { search }
    } = this.props
    const token = getUrlParameter('token', search)
    const shipperId = getUrlParameter('shipper_id', search)
    if (token && shipperId) {
      this.props.loginWithToken({ token, shipperId })
      this.transition({ type: 'LOGIN' })
    }
  }

  componentDidUpdate (prevProps) {
    const { error, loading } = this.props
    if (prevProps.loading && !loading && error) {
      this.transition({ type: 'FAILURE' })
    }
  }

  transition = event => {
    const nextAuthState = authMachine.transition(this.state.authState, event.type)
    const nextState = nextAuthState.actions.reduce((state, action) => this.command(action, event) || state, undefined)
    this.setState({
      authState: nextAuthState.value,
      ...nextState
    })
  }

  command = (action, event) => {
    switch (action) {
      case 'loginProcess':
        this.start()
        break
      default:
        break
    }
  }

  goBack = () => this.transition({ type: 'BACK' })

  tick = () => {
    this.setState({ percent: this.state.percent + 5 })
  }

  start = () => {
    this.interval = setInterval(this.tick, 200)
  }

  UNSAFE_componentWillUnmount () {
    clearInterval(this.interval)
  }

  handleChange = values => {
    this.setState(values)
  }

  handleSubmit = data => {
    this.props.loginRequest(data)
  }

  render () {
    const { addShipper, isAuthenticated, loginRequest, loginWithToken, ...props } = this.props
    const { authState, percent, ...values } = this.state

    switch (authState) {
      case 'loggedIn':
        return <Navigate replace to={ROUTES.HOME} />
      case 'forgotEmail':
        return <ForgotEmailForm {...props} backAction={this.goBack} action={this.goBack} />
      case 'forgotPassword':
        return (
          <ForgotPasswordForm
            {...props}
            addShipper={addShipper}
            backAction={this.goBack}
            emailAction={() => this.transition({ type: 'FORGOT_EMAIL' })}
          />
        )
      case 'loggingIn':
        return <DialogBox messages={{ title: 'logging_in', percent }} />
      default:
        return (
          <DialogBox
            {...props}
            values={values}
            onChange={this.handleChange}
            action={addShipper ? props.action : this.handleSubmit}
            emailAction={() => this.transition({ type: 'FORGOT_EMAIL' })}
            passwordAction={() => this.transition({ type: 'FORGOT_PASSWORD' })}
            messages={addShipper ? props.messages : messages}
            addShipper={addShipper}
          />
        )
    }
  }
}

LoginForm.propTypes = {
  isAuthenticated: PropTypes.bool.isRequired,
  loginRequest: PropTypes.func,
  dismissError: PropTypes.func,
  addShipper: PropTypes.func,
  loginWithToken: PropTypes.func,
  error: PropTypes.object,
  loading: PropTypes.bool,
  location: PropTypes.object
}

const mapStateToProps = createStructuredSelector({
  isAuthenticated: selectIsAuthenticated(),
  error: selector('global', 'user', 'error')(),
  loading: selector('global', 'user', 'loading')()
})

function mapDispatchToProps (dispatch) {
  return {
    loginRequest: data => dispatch(userCreators.loginRequest(data)),
    loginWithToken: data => dispatch(userCreators.loginWithToken(data)),
    dismissError: () => dispatch(userCreators.clearError())
  }
}

const withConnect = connect(mapStateToProps, mapDispatchToProps)
const withSaga = injectSaga({ key: 'loginForm', saga })

export default compose(withSaga, withLocation, withConnect)(LoginForm)
